import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import PropTypes from "prop-types";
import {
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Typography,
} from "@mui/material";
import { withTranslation } from "react-i18next";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import {
  CONSUMER,
  minHtContractDemandLoad,
  premiseNamesForTimeShift,
} from "common/constant";
import {
  loadOptions,
  unitOptions,
  gstOptions,
  timeshiftingOptions,
  getOptionValue,
} from "common/enums";

import { onlyNumberKeys } from "utils";

import { useConsumersQuery } from "apis/User";
import { usePremiseTypeQuery } from "apis/PremiseType";
import { useApplicantTypeQuery } from "apis/ApplicantType";

const SINGLE_PHASE = 1;
const THREE_PHASE = 2;

const htLoadOptions = [{ value: THREE_PHASE, label: "Three Phase" }];

const tentionOptions = [
  { value: "LT", label: "LT" },
  { value: "HT", label: "HT" },
];

const premiseNames = ["temporary"];

const singlePhaseOptions = [{ value: 240, label: "240 V" }];

const ChangeCategoryDetail = ({ formik, t }) => {
  const { roles } = useSelector((state) => state.Login);

  const { data: { data: { items: applicantType } = {} } = {} } =
    useApplicantTypeQuery();

  const {
    values: {
      id,
      consumerNumber,
      applicationTypeId,
      applicant: { applicantTypeId, isGSTRegistered },
      connectionLoad: {
        powerRequirement,
        loadDetails,
        tention,
        supplyVoltage,
        timeShift,
      },
      utilityAddress: {
        premiseTypeId,
        isBPL,
        // isRural
      },
      extraValues: { isPanNumber, isGstNumber },
      inProgressApplicationId,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
    },
    setFieldValue,
  } = formik;

  const { data: { data: consumersOptions } = {} } = useConsumersQuery({
    applicationType: applicationTypeId,
    utilityId: getOptionValue("utilityOptions", "Power"),
  });
  const { data: { data: premiseType } = {} } = usePremiseTypeQuery({
    utilityId: 1,
  });

  const [premiseIds, setPremiseIds] = useState([]);
  const [supplyVoltageOptions, setSupplyVoltageOptions] = useState([]);
  const [premiseIdsForTimeShift, setPremiseIdsForTimeShift] = useState([]);

  const threePhaseOptions = useMemo(
    () =>
      tention === tentionOptions[1].value
        ? [
            { value: 33, label: "33 kV" },
            { value: 11, label: "11 kV" },
            { value: 415, label: "415 V" },
          ]
        : [{ value: 415, label: "415 V" }],
    [tention]
  );

  const isIndividualApplicantType = useMemo(
    () =>
      applicantType?.find(
        (application) => application.name.toLowerCase() === "individual"
      ).id === applicantTypeId,
    [applicantType, applicantTypeId]
  );

  const isDisable = useMemo(
    () =>
      (inProgressApplicationId !== id && !isAllApplicationsProcessCompleted) ||
      payableBillAmount > 0,
    [
      inProgressApplicationId,
      id,
      isAllApplicationsProcessCompleted,
      payableBillAmount,
    ]
  );

  useEffect(() => {
    const updatedPremiseIds = [];
    premiseType?.forEach((premise) => {
      if (premiseNames.includes(premise.name.toLowerCase())) {
        updatedPremiseIds.push(premise.id);
      }
    });

    setPremiseIds(updatedPremiseIds);
  }, [premiseType]);

  useEffect(() => {
    if (tention === tentionOptions[0].value) {
      if (premiseIds.includes(premiseTypeId)) {
        setFieldValue(
          "connectionLoad.powerRequirementUnit",
          unitOptions[2].value
        );
      } else {
        setFieldValue(
          "connectionLoad.powerRequirementUnit",
          unitOptions[0].value
        );
      }
    } else {
      setFieldValue(
        "connectionLoad.powerRequirementUnit",
        unitOptions[1].value
      );
    }
  }, [tention, premiseTypeId, premiseIds, setFieldValue]);

  useEffect(() => {
    const updatedPremiseIdsForTimeShift = [];
    premiseType?.forEach((premise) => {
      if (premiseNamesForTimeShift.includes(premise.name.toLowerCase())) {
        updatedPremiseIdsForTimeShift.push(premise.id);
      }
    });

    setPremiseIdsForTimeShift(updatedPremiseIdsForTimeShift);
  }, [premiseType]);

  useEffect(() => {
    if (supplyVoltage) {
      if (supplyVoltage === 415 || supplyVoltage === 240) {
        setFieldValue("connectionLoad.supplyVoltageUnit", 4);
      } else {
        setFieldValue("connectionLoad.supplyVoltageUnit", 5);
      }
    }
  }, [supplyVoltage, setFieldValue]);

  useEffect(() => {
    const options =
      loadDetails === SINGLE_PHASE
        ? singlePhaseOptions
        : loadDetails === THREE_PHASE
        ? threePhaseOptions
        : [];

    if (
      supplyVoltage &&
      !options.some((option) => option.value === supplyVoltage)
    ) {
      setFieldValue("connectionLoad.supplyVoltage", "");
    }
    setSupplyVoltageOptions(options);
  }, [loadDetails, tention]);

  const isResidential = useMemo(
    () =>
      premiseTypeId &&
      premiseType &&
      premiseTypeId ===
        premiseType?.find(
          (premise) => premise.name.toLowerCase() === "residential"
        )?.id,
    [premiseTypeId, premiseType]
  );

  // for empty the formik value when load options are not matching its value

  useEffect(() => {
    const options =
      tention === tentionOptions[0].value && powerRequirement <= 6
        ? loadOptions
        : htLoadOptions;

    if (!options.some((option) => option.value === loadDetails)) {
      setFieldValue("connectionLoad.loadDetails", "");
    }
  }, [tention, powerRequirement]);

  // select time shift value based on premise type and while editing application
  useEffect(() => {
    if (premiseIdsForTimeShift.length) {
      if (timeShift && !premiseIdsForTimeShift.includes(premiseTypeId)) {
        setFieldValue("connectionLoad.timeShift", "");
      }
      if (premiseIdsForTimeShift.includes(premiseTypeId) && !timeShift) {
        setFieldValue("connectionLoad.timeShift", timeshiftingOptions[0].value);
      }
    }
  }, [premiseTypeId, premiseIdsForTimeShift]);

  useEffect(() => {
    if (premiseTypeId && premiseType) {
      let isBPLChecked = isBPL || false;
      // let isRuralChecked = isRural || false;

      if (!isResidential) {
        isBPLChecked = "";
        // isRuralChecked = "";
      }
      if (isBPLChecked !== isBPL)
        setFieldValue("extraValues.isBPL", isBPLChecked);
      // if (isRuralChecked !== isRural)
      //   setFieldValue("extraValues.isRural", isRuralChecked);
    }
  }, [isResidential]);

  return (
    <>
      {roles.includes(CONSUMER) && (
        <Row>
          <Col md={4} sm={6}>
            <SelectField
              label={t("Customer Number") + "*"}
              name="utilityConsumerId"
              formik={formik}
              options={consumersOptions || []}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.consumerNumber}
            />
          </Col>
        </Row>
      )}
      <Typography className="mb-3" lineHeight="1" variant="h6">
        {t("Customer Details")}:
      </Typography>
      <Row>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Customer Name")}
            name="extraValues.consumerName"
            formik={formik}
            disabled
          />
        </Col>

        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Current Tariff Category")}
            name="extraValues.tariffName"
            formik={formik}
            disabled
          />
        </Col>
        {!isPanNumber && (
          <Col md={4} sm={6}>
            <InputFieldText
              label={
                isIndividualApplicantType
                  ? t("PAN Number")
                  : t("PAN Number") + "*"
              }
              name="applicant.panNumber"
              formik={formik}
              disabled={isDisable}
              onChange={(e) =>
                setFieldValue(
                  "applicant.panNumber",
                  e.target.value.toUpperCase()
                )
              }
              placeholder={
                isIndividualApplicantType
                  ? t("PAN Number")
                  : t("PAN Number") + "*"
              }
            />
          </Col>
        )}
        {!isGstNumber && (
          <Col md={4} sm={6}>
            <SelectField
              label={t("GST") + "*"}
              name="applicant.isGSTRegistered"
              onChange={(_, { value }) => {
                setFieldValue("applicant.isGSTRegistered", value);
                if (!value) {
                  setFieldValue("applicant.gstNumber", "");
                }
              }}
              formik={formik}
              disabled={isDisable}
              options={gstOptions}
            />
          </Col>
        )}
        {!isGstNumber && isGSTRegistered && (
          <Col md={4} sm={6}>
            <InputFieldText
              label={t("GST Number") + "*"}
              name="applicant.gstNumber"
              formik={formik}
              onChange={(e) =>
                setFieldValue(
                  "applicant.gstNumber",
                  e.target.value.toUpperCase()
                )
              }
              placeholder="GST Number"
              disabled={isDisable}
            />
          </Col>
        )}
      </Row>
      <Typography className="mb-3" lineHeight="1" variant="h6">
        {t("Load Details")}:
      </Typography>
      <Row>
        <Col md={4} sm={6}>
          <SelectField
            label={t("Premise Type") + "*"}
            name="utilityAddress.premiseTypeId"
            formik={formik}
            options={premiseType}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            disabled={isDisable}
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={t("Premise Usage Purpose") + "*"}
            name="utilityAddress.premiseUsagePurpose"
            formik={formik}
            placeholder="Enter Premise Usage Purpose"
            disabled={isDisable}
          />
        </Col>
        <Col md={4}>
          <SelectField
            label={t("Power Requirements") + "*"}
            name="connectionLoad.tention"
            formik={formik}
            options={tentionOptions}
            placeholder="Tention"
            disabled
          />
        </Col>
        <Col md={4}>
          <SelectField
            label={t("Load Details") + "*"}
            name="connectionLoad.loadDetails"
            formik={formik}
            options={
              tention
                ? tention === tentionOptions[0].value && powerRequirement <= 6
                  ? loadOptions
                  : htLoadOptions
                : []
            }
            placeholder="Select Supply Voltage"
            disabled
          />
        </Col>

        <Col md={4}>
          <SelectField
            label={t("Supply Voltage") + "*"}
            name="connectionLoad.supplyVoltage"
            formik={formik}
            options={supplyVoltageOptions}
            placeholder="Select Supply Voltage"
            disabled
          />
        </Col>
        <Col md={4} sm={6}>
          <InputFieldText
            label={
              premiseIds.includes(premiseTypeId)
                ? tention === tentionOptions[0].value
                  ? t("Contract Load") + "*"
                  : t("Contract Demand") + "*"
                : powerRequirement < minHtContractDemandLoad
                ? t("Contract Load") + "*"
                : t("Contract Demand") + "*"
            }
            name="connectionLoad.powerRequirement"
            formik={formik}
            type="number"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {tention === tentionOptions[0].value &&
                  premiseIds.includes(premiseTypeId)
                    ? powerRequirement <= 125
                      ? unitOptions[2].label
                      : unitOptions[1].label
                    : powerRequirement < 151
                    ? unitOptions[0].label
                    : unitOptions[1].label}
                </InputAdornment>
              ),
              inputProps: { min: 0, onKeyDown: onlyNumberKeys },
            }}
            disabled
          />
        </Col>

        {premiseIdsForTimeShift.includes(premiseTypeId) && (
          <Col md={4}>
            <SelectField
              label={t("Time shift") + "*"}
              name="connectionLoad.timeShift"
              formik={formik}
              options={timeshiftingOptions || []}
              placeholder="Select time shift"
              disabled
            />
          </Col>
        )}
      </Row>
      <Row>
        {isResidential && (
          <Row xs={12}>
            <Col md={4} xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!isBPL}
                    onChange={() =>
                      setFieldValue("utilityAddress.isBPL", !isBPL)
                    }
                  />
                }
                label={t("BPL")}
                disabled
              />
            </Col>

            {/* <Col md={4} s={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={!!isRural}
                    onChange={() =>
                      setFieldValue("utilityAddress.isRural", !isRural)
                    }
                  />
                }
                label="Rural"
              />
            </Col> */}
          </Row>
        )}
      </Row>
      {!!inProgressApplicationId &&
      inProgressApplicationId !== id &&
      !isAllApplicationsProcessCompleted &&
      !!consumerNumber ? (
        <Typography variant="body2" color="error">
          {t(
            "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
          )}
        </Typography>
      ) : payableBillAmount > 0 ? (
        <Typography variant="body2" color="error">
          You have an unpaid amount of {payableBillAmount} RS. Please pay first
          to continue.
        </Typography>
      ) : null}
    </>
  );
};

ChangeCategoryDetail.propTypes = {
  formik: PropTypes.object,
  t: PropTypes.func,
};
export default withTranslation()(ChangeCategoryDetail);
